import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import classNames from 'classnames';
import useDimensions from 'react-cool-dimensions';
/**
 * This widget will create a horizontal scroll-able list
 *
 * @example
 * ```
 *  <HorizontalScrollList
 *      items={[...Array(15).keys()]}
 *      renderItem={(item, key) => {
 *      return (<div className="item-thumbnail">
 *              {key}
 *          </div>)
 *      }}
 *  />
 * ```
 * @export
 */
const HorizontalScrollList = (props) => {
    let { items, renderItem, scrollStep = 1, className, infinite, autoScroll } = props;
    let [showPrev, setShowPrev] = React.useState(false);
    let [showNext, setShowNext] = React.useState(false);
    const currentKey = React.useRef(0);
    const listItemsRef = React.useRef([]);
    const listRef = React.useRef(null);
    const { width, height, entry, unobserve, observe } = useDimensions({
        onResize: (p) => { },
    });
    React.useEffect(() => {
        toggleControllers();
        // in case it takes some times to render the items 
        // happened when using in side modals 
        if (infinite && (autoScroll === null || autoScroll === void 0 ? void 0 : autoScroll.enable)) {
            setInterval(() => {
                scrollOnclick('right');
            }, (autoScroll === null || autoScroll === void 0 ? void 0 : autoScroll.interval) || 5000);
        }
        else {
            setTimeout(() => {
                toggleControllers();
            }, 300);
        }
    }, [width, height, items, autoScroll, infinite]);
    // scroll on click
    const scrollOnclick = (direction) => {
        let _currentKey = currentKey.current;
        if (infinite) {
            const { firstItemIsVisible, lastItemIsVisible } = getItemsVisibility();
            if (direction == 'left') {
                if (firstItemIsVisible) {
                    _currentKey = items.length - 1;
                }
                else {
                    _currentKey -= scrollStep;
                }
            }
            else {
                if (lastItemIsVisible) {
                    _currentKey = 0;
                }
                else {
                    _currentKey += scrollStep;
                }
            }
            currentKey.current = _currentKey;
            scrollItemToView(_currentKey);
            return;
        }
        if (direction == "left") {
            _currentKey -= scrollStep;
            if (_currentKey < 0)
                _currentKey = 0;
        }
        else {
            _currentKey += scrollStep;
            if (_currentKey >= (items.length - 1)) {
                _currentKey = (items.length - 1);
            }
        }
        currentKey.current = _currentKey;
        scrollItemToView(_currentKey);
    };
    // scroll
    const scrollItemToView = (key) => {
        let scrollLeft = 0;
        items.map((item, index) => {
            if (index < key) {
                let nextItemDetails = listItemsRef.current[index].getBoundingClientRect();
                scrollLeft += nextItemDetails.width;
            }
        });
        listRef.current.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
        });
    };
    function getItemsVisibility() {
        let listBoundaries = listRef.current.getBoundingClientRect();
        let firstItemBoundaries = listItemsRef.current[0].getBoundingClientRect();
        let lastItemBoundaries = listItemsRef.current[items.length - 1].getBoundingClientRect();
        const firstItemIsVisible = !(listBoundaries.left > firstItemBoundaries.left);
        const lastItemIsVisible = !(listBoundaries.right < lastItemBoundaries.right - 1);
        return {
            firstItemIsVisible,
            lastItemIsVisible
        };
    }
    const toggleControllers = () => {
        if (infinite) {
            setShowPrev(true);
            setShowNext(true);
            return;
        }
        if (items.length > 0) {
            const { firstItemIsVisible, lastItemIsVisible } = getItemsVisibility();
            setShowPrev(!firstItemIsVisible);
            setShowNext(!lastItemIsVisible);
        }
        else {
            setShowPrev(false);
            setShowNext(false);
        }
    };
    return (_jsxs("div", { className: classNames("uxpcore_hscrolllist__container hs-list-container", className), ref: observe, children: [_jsx("div", { className: "uxpcore_hscrolllist__items hs-list-items", ref: listRef, onScroll: () => { toggleControllers(); }, children: items.map((item, key) => {
                    let itemRef = (el) => (listItemsRef.current[key] = el);
                    return (_jsx("div", { className: "uxpcore_hscrolllist__item hs-list-item", ref: itemRef, children: renderItem(item, key) }, key));
                }) }), showPrev &&
                _jsx("div", { className: "uxpcore_hscrolllist__controller uxpcore_hscrolllist__controller--prev controllers prev", onClick: () => { scrollOnclick("left"); } }), showNext &&
                _jsx("div", { className: "uxpcore_hscrolllist__controller uxpcore_hscrolllist__controller--next controllers next", onClick: () => { scrollOnclick("right"); } })] }));
};
export default HorizontalScrollList;
